import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { SiteFooter, SiteNav } from "@/components/media/SiteNav";
import { Eyebrow, Hero, LessonCard, Section, StoryCard, Timeline, TribalRule } from "@/components/media/Sections";
import {
  AcademyModules,
  Community,
  EventEconomics,
  Faq,
  JourneyFeed,
  OperatorPaths,
  Realities,
} from "@/components/media/Blocks";
import { ApplyForm, ApplyIntro } from "@/components/media/ApplyForm";
import { FlavorBar, FlavorWall } from "@/components/media/FlavorWall";
import { ReelWall } from "@/components/media/Reels";
import { LicenseBlock } from "@/components/media/License";
import { Credibility } from "@/components/ladder/Credibility";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const site = useSite();

  if (!site)
    return (
      <div>
        <SiteNav />
        <div className="mono px-5 py-32 text-center text-sm text-muted-foreground">
          Loading the Smash HQ…
        </div>
      </div>
    );

  const stories = site.stories || [];
  const lessons = site.lessons || [];

  return (
    <div data-testid="home-page">
      <SiteNav />

      {/* 1 — HERO */}
      <Hero hero={site.hero} />
      <FlavorBar testId="hero-flavor-bar" />

      {/* 2 — LEMONADING */}
      <Section testId="lemonading-section" id="lemonading" texture="graffiti">
        <Eyebrow>THE MOVEMENT</Eyebrow>
        <h2 className="neon mt-3 text-5xl leading-none sm:text-6xl lg:text-7xl">
          {site.lemonading.title}
        </h2>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white md:text-2xl">
          {site.lemonading.definition}
        </p>
        <p className="brand-script mt-8 max-w-2xl text-2xl text-primary md:text-3xl">
          “{site.lemonading.question}”
        </p>

        {stories.length ? (
          <>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {stories.slice(0, 3).map((s) => (
                <StoryCard key={s.id} story={s} />
              ))}
            </div>
            <Link
              to="/lemonading"
              data-testid="lemonading-all"
              className="display mt-10 inline-flex items-center gap-2 text-xl uppercase text-primary hover:underline"
            >
              All stories <ArrowRight size={16} />
            </Link>
          </>
        ) : (
          <p className="mono mt-10 text-[11px] tracking-widest text-muted-foreground">
            {site.lemonading.empty_note}
          </p>
        )}
      </Section>

      {/* 3 — THE SMASH JOURNEY */}
      {(site.journeyClips || []).length ? (
        <Section testId="journey-section" id="journey" tint>
          <JourneyFeed
            journey={site.smashJourney}
            clips={site.journeyClips}
            categories={site.clip_categories || []}
          />
        </Section>
      ) : null}

      {/* 3b — THE REEL WALL */}
      {(site.reels || []).length ? (
        <Section testId="reels-section" id="reels">
          <ReelWall intro={site.reelsIntro || {}} reels={site.reels} />
        </Section>
      ) : null}

      {/* 4 — FREE BUSINESS LESSONS */}
      <Section testId="learn-section" id="learn">
        <Eyebrow>TEACH FIRST</Eyebrow>
        <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{site.lessonsIntro.title}</h2>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
          {site.lessonsIntro.intro}
        </p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lessons.slice(0, 6).map((l) => (
            <LessonCard key={l.id} lesson={l} />
          ))}
        </div>
        <Link
          to="/learn"
          data-testid="learn-all"
          className="display mt-10 inline-flex items-center gap-2 text-xl uppercase text-primary hover:underline"
        >
          All {lessons.length} lessons <ArrowRight size={16} />
        </Link>
      </Section>

      {/* 5 — THE SMASH STORY */}
      <Section testId="timeline-section" id="our-story" tint texture="tribal">
        <Timeline timeline={site.timeline} />
      </Section>

      <TribalRule testId="operators-tribal-1" />

      {/* 6 — WHAT SMASH ACADEMY TEACHES */}
      <Section testId="academy-section" id="academy" texture="graffiti">
        <AcademyModules pitch={site.academyPitch} />
      </Section>

      {/* PROOF — real photos and footage */}
      <div className="border-y border-border bg-[#0d0d0d]">
        <Credibility />
      </div>

      {/* THE DRINKS — color from the real product */}
      <Section testId="flavor-section" id="flavors">
        <FlavorWall />
      </Section>

      {/* 7 — REALITIES */}
      <Section testId="realities-section" texture="slime">
        <Realities realities={site.realities} />
      </Section>

      {/* 8 — EVENT ECONOMICS */}
      <Section testId="economics-section" id="economics" tint>
        <EventEconomics economics={site.economics} />
      </Section>

      {/* 9 — OPERATOR PATHS */}
      <Section testId="paths-section" id="paths" texture="tribal">
        <OperatorPaths paths={site.operator_paths || []} disclaimer={site.disclaimer} />
      </Section>

      <TribalRule testId="operators-tribal-2" />

      {/* 9b — THE ONE-YEAR LICENSE */}
      <Section testId="license-section" id="license" tint>
        <LicenseBlock license={site.license} />
      </Section>

      {/* 10 — BUILDING SMASH IN PUBLIC */}
      <Section testId="founder-section" tint>
        <Eyebrow>{site.founderFeed.note}</Eyebrow>
        <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{site.founderFeed.title}</h2>
        <p className="mt-5 max-w-3xl text-base text-muted-foreground md:text-lg">
          {site.founderFeed.body}
        </p>
        <Link
          to="/our-story"
          data-testid="founder-story-link"
          className="display mt-8 inline-flex items-center gap-2 text-xl uppercase text-primary hover:underline"
        >
          Meet Alvin & Moana <ArrowRight size={16} />
        </Link>
      </Section>

      {/* 11 — COMMUNITY */}
      <Section testId="community-section" texture="graffiti">
        <Community community={site.community} />
      </Section>

      {/* 12 — FAQ */}
      <Section testId="faq-section" tint>
        <Faq faq={site.faq} />
      </Section>

      {/* 13 — APPLY */}
      <Section testId="apply-section" id="apply">
        <ApplyIntro apply={site.apply} />
        <div className="mt-10 max-w-3xl">
          <ApplyForm apply={site.apply} />
        </div>
      </Section>

      <SiteFooter />
    </div>
  );
}
